import is from '@sindresorhus/is';
import { noop } from 'lodash';
import type { FunctionComponent } from 'react';
import { useContext } from 'react';

import { ApplicationContext } from '~/shared/context/context';
import { useApplicationContext } from '~/shared/context/use-application-context';
import { SkeletonBase } from '~/ui/core/skeleton/Skeleton';
import { SidepanelHeader } from '~/ui/domain/Sidepanel/components/SidepanelHeader';

import { useTileNavigation } from '../../url-tiles';
import { Action as ActionWrapper } from '../components/Action';
import {
    ContentContainer,
    ContentSections,
    Section,
    SectionContent,
    SectionHeader,
    StyledStickyHeader,
    Wrapper,
} from '../components/Common';
import {
    CategoryListSkeleton,
    DescriptionSectionSkeleton,
    SkeletonBreadcrumbs,
    TitleSkeleton,
} from '../components/SkeletonLoaders';
import { AuditTableSkeleton } from './AuditTable';

export const LoadingContent: FunctionComponent<{ readonly tileKey?: string }> = ({ tileKey }) => {
    const { translate } = useContext(ApplicationContext);
    const { applicationState } = useApplicationContext();
    const isReviewStatusTableEnabled = applicationState.featureFlagService.isFeatureFlagEnabled('review-status-table');
    const tileNavigation = useTileNavigation(tileKey);

    const stickyHeader = (
        <StyledStickyHeader $paddingBottomToken={isReviewStatusTableEnabled ? 'm' : 'xxl'}>
            <SidepanelHeader
                actions={undefined}
                activeLanguage={undefined}
                disableArrows={is.undefined(tileNavigation)}
                isGenerating={false}
                isLanguagePickerDisabled={true}
                languages={[]}
                setActiveLanguage={noop}
                onClose={noop}
                onNext={tileNavigation?.onNext}
                onPrevious={tileNavigation?.onPrevious}
            />
        </StyledStickyHeader>
    );

    if (isReviewStatusTableEnabled) {
        return (
            <div className="flex w-full min-w-0 flex-col gap-0" data-testid="loading-content">
                {stickyHeader}
                <SkeletonBreadcrumbs />
                <div className="mt-12xl">
                    <TitleSkeleton />
                </div>
                <ContentContainer>
                    <div className="mt-12xl flex flex-col gap-xxs overflow-hidden rounded-xl bg-gray-100 p-xxs shadow-sm">
                        <ActionWrapper $surface="integrated">
                            <div className="flex min-w-0 items-start">
                                <SkeletonBase className="h-[32px] w-full max-w-[398px] rounded-lg" />
                            </div>
                            <div className="flex min-w-0 justify-end">
                                <SkeletonBase className="h-[36px] w-[131px] rounded-lg" />
                            </div>
                        </ActionWrapper>
                        <AuditTableSkeleton />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'row', gap: '16px', marginTop: '2.1rem' }}>
                        <ContentSections>
                            <Section>
                                <SectionHeader>
                                    {translate('pages.job-architecture-v2.job-profile-title')}
                                </SectionHeader>
                                <DescriptionSectionSkeleton />
                                <DescriptionSectionSkeleton />
                                <DescriptionSectionSkeleton />
                            </Section>
                            <Section>
                                <SectionHeader>
                                    {translate('pages.job-architecture-v2.job-profile-required-skills')}
                                </SectionHeader>
                                <SectionContent>
                                    <CategoryListSkeleton />
                                </SectionContent>
                            </Section>
                        </ContentSections>
                    </div>
                </ContentContainer>
            </div>
        );
    }

    return (
        <Wrapper data-testid="loading-content">
            {stickyHeader}
            <SkeletonBreadcrumbs />
            <div style={{ marginBottom: '-1.8rem', marginTop: '3.3rem' }}>
                <TitleSkeleton />
            </div>
            <ActionWrapper>
                <div style={{ height: '2.2rem' }} />
            </ActionWrapper>
            <ContentContainer>
                <div style={{ display: 'flex', flexDirection: 'row', gap: '16px', marginTop: '2.1rem' }}>
                    <ContentSections>
                        <Section>
                            <SectionHeader>{translate('pages.job-architecture-v2.job-profile-title')}</SectionHeader>
                            <DescriptionSectionSkeleton />
                            <DescriptionSectionSkeleton />
                            <DescriptionSectionSkeleton />
                        </Section>
                        <Section>
                            <SectionHeader>
                                {translate('pages.job-architecture-v2.job-profile-required-skills')}
                            </SectionHeader>
                            <SectionContent>
                                <CategoryListSkeleton />
                            </SectionContent>
                        </Section>
                    </ContentSections>
                </div>
            </ContentContainer>
        </Wrapper>
    );
};
